import React, { forwardRef, PropsWithoutRef, RefAttributes, PropsWithChildren } from 'react';
import { useRouter } from 'next/router';
import { Props as LinkProps } from '../components/Link';
import Tracking from '../utils/tracking';

interface WithTrackingProps {
  'aria-label'?: string;
  'data-tracking-label'?: string;
  onClick?: React.MouseEventHandler;
  href?: LinkProps['href'];
}

const withTracking =
  (eventName: string, properties = {}) =>
  <T extends WithTrackingProps>(
    Component: React.ComponentType<T>,
  ): React.ForwardRefExoticComponent<PropsWithoutRef<T> & RefAttributes<unknown>> =>
    forwardRef(({ onClick, ...props }: PropsWithChildren<T>, ref): JSX.Element => {
      const router = useRouter();
      const { asPath } = router || {};

      const handleClick = (e: React.MouseEvent) => {
        if (onClick) onClick(e);

        let label = props['data-tracking-label'] || props['aria-label'] || 'unknown';
        if (label === 'unknown' && typeof props.children === 'string') {
          label = props.children as string;
        }

        Tracking.event(eventName, {
          ...properties,
          label,
          path: asPath,
          ...(props.href && { href: props.href }),
        });
      };

      return <Component ref={ref} onClick={handleClick} {...(props as T)} />;
    });

export default withTracking;
